import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Result, Spin } from "antd";
import { FormattedMessage } from "react-intl";
import { useDispatch } from "react-redux";
import Header from "../../components/Header/Header";
import PayoutEmailModal from "../../components/Stripe/PayoutEmailModal";
import { useConnectOnboarding } from "../../hooks/useConnectOnboarding";
import { readAuthToken } from "../../utils/axiosMiddleware";
import { returnToApp } from "../../utils/appDeepLink";
import {
  getRefreshCopy,
  isMintFailure,
  REFRESH_STATES,
  resolveRefreshState,
  shouldMintLink,
} from "./refreshState";

/**
 * Connect `refresh_url` target (`/stripe/refresh`).
 *
 * Account Links are single-use, so Stripe sends anyone holding an expired or
 * already-opened one here. Before this route existed they hit the catch-all:
 * signed in they were bounced to the homepage, signed out they went to
 * `/login?next=` and straight back into the catch-all, looping.
 *
 * Separate from `StripeOnboarding` rather than another mode on it: that
 * component keys its return handling on `pathname === ROUTES.STRIPE_RETURN`, so
 * this path would fall through to the normal setup screen.
 *
 * Mobile reaches this in the SYSTEM browser (the app opens Stripe with
 * `Linking.openURL`, not a WebView), where there is no session — hence the
 * app deep link beside the sign-in button, offered without trying to detect the
 * platform. `buildAppDeepLink` falls back to `gigmi://app` without a user id,
 * which is a prefix mobile's linking config registers.
 */
export const StripeRefresh = () => {
  const dispatch = useDispatch();
  const {
    startOnboarding,
    loading,
    emailRequired,
    submitPayoutEmail,
    dismissEmailPrompt,
  } = useConnectOnboarding(dispatch, { trackStatus: false });

  const userId = readAuthToken()?.user_id;
  const signedIn = Boolean(userId);
  const [mintFailed, setMintFailed] = useState(false);
  // StrictMode runs mount effects twice in development; without this the payee
  // gets two Account Links minted for one arrival.
  const mintedRef = useRef(false);

  const state = resolveRefreshState({ signedIn, mintFailed });
  const { title, description } = getRefreshCopy(state);

  // Ask what happened rather than inferring it: `window.location.assign` does
  // not halt execution, so the await resolves on the success path too. Treating
  // that as failure flashed "We couldn't reopen payout setup" over a redirect
  // that was working — and on a slow connection it sat there long enough to tap
  // Try again, minting a second link that raced the navigation.
  //
  // `emailRequired` is also not a failure: the modal takes over and re-calls
  // `startOnboarding` once an address is supplied.
  const mint = useCallback(async () => {
    setMintFailed(false);
    setMintFailed(isMintFailure(await startOnboarding()));
  }, [startOnboarding]);

  // The modal is the other way a mint starts, and it has two endings that also
  // leave the payee with nowhere to go: submitting an address and having the
  // mint fail behind it, or dismissing the prompt entirely. Both used to sit on
  // a spinner forever.
  const onSubmitEmail = useCallback(
    async (email) => {
      setMintFailed(isMintFailure(await submitPayoutEmail(email)));
    },
    [submitPayoutEmail]
  );

  const onDismissEmail = useCallback(() => {
    dismissEmailPrompt();
    setMintFailed(true);
  }, [dismissEmailPrompt]);

  const retry = useCallback(() => {
    mintedRef.current = true;
    mint();
  }, [mint]);

  useEffect(() => {
    if (!shouldMintLink(state) || mintedRef.current) {
      return;
    }
    mintedRef.current = true;
    mint();
  }, [state, mint]);

  const actions = [
    state === REFRESH_STATES.FAILED && (
      <Button
        key="retry"
        type="primary"
        className="fw-bold"
        loading={loading}
        onClick={retry}
        data-testid="retry-payout-link"
      >
        <FormattedMessage id="TRY_AGAIN" />
      </Button>
    ),
    state === REFRESH_STATES.SIGNED_OUT && (
      <Button
        key="signin"
        type="primary"
        className="fw-bold"
        href={`/login?next=${encodeURIComponent(window.location.pathname)}`}
        data-testid="refresh-sign-in"
      >
        <FormattedMessage id="SIGN_IN_TO_CONTINUE" />
      </Button>
    ),
    /* Mobile arrives here in the system browser with no session, so signing in
       on the web is the wrong repair — the app is where they were. */
    state === REFRESH_STATES.SIGNED_OUT && (
      <Button
        key="app"
        onClick={() => returnToApp(userId)}
        data-testid="refresh-open-app"
      >
        <FormattedMessage id="OPEN_GIGMI_APP" />
      </Button>
    ),
  ].filter(Boolean);

  return (
    <>
      <Header />
      <div className="container mt-4">
        <Result
          status={state === REFRESH_STATES.FAILED ? "warning" : "info"}
          icon={
            state === REFRESH_STATES.MINTING ? <Spin size="large" /> : undefined
          }
          title={<FormattedMessage id={title} />}
          subTitle={<FormattedMessage id={description} />}
          extra={actions.length ? actions : undefined}
        />
        {/* Minting can come back asking for an email — an SMS-invited payee has
            none, and Stripe will not open an account without one. */}
        <PayoutEmailModal
          open={emailRequired}
          loading={loading}
          onSubmit={onSubmitEmail}
          onCancel={onDismissEmail}
        />
      </div>
    </>
  );
};

export default StripeRefresh;
